import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const DEFAULT_VOICE_ID = "xsfXCyY98Gbq4cV85MC1";
const ALLOWED_VOICE_IDS = new Set([
  "xsfXCyY98Gbq4cV85MC1", // Gentle Gemma (whisper)
  
  "2qfp6zPuviqeCOZIE9RZ", // Female / neutral
  "J8Jo6V3F3HsRR4u13XbM", // New narrator voice
]);

const InputSchema = z.object({
  text: z.string().min(1).max(5000),
  voiceId: z.string().min(8).max(64).optional(),
});

type TtsResult =
  | { audioBase64: string; mimeType: string; error?: undefined; fallback?: undefined }
  | { audioBase64?: undefined; mimeType?: undefined; error: string; fallback: boolean };

export const synthesizeSpeech = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => InputSchema.parse(data))
  .handler(async ({ data }): Promise<TtsResult> => {
    const voiceId =
      data.voiceId && ALLOWED_VOICE_IDS.has(data.voiceId)
        ? data.voiceId
        : DEFAULT_VOICE_ID;

    // 1) Preferred provider: ElevenLabs.
    try {
      const apiKey = process.env.ELEVENLABS_API_KEY;
      if (apiKey) {
        const response = await fetch(
          `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}?output_format=mp3_44100_64`,
          {
            method: "POST",
            headers: {
              "xi-api-key": apiKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: data.text,
              // Turbo v2.5 — multilingual, lowest-latency ElevenLabs model.
              model_id: "eleven_turbo_v2_5",
              voice_settings: {
                stability: 0.55,
                similarity_boost: 0.75,
                style: 0,
                use_speaker_boost: true,
                speed: 0.95,
              },
            }),
          },
        );

        if (response.ok) {
          const buf = await response.arrayBuffer();
          return {
            audioBase64: Buffer.from(buf).toString("base64"),
            mimeType: "audio/mpeg",
          };
        }
        const errText = await response.text().catch(() => "");
        console.error(`ElevenLabs TTS ${response.status}: ${errText} — falling back`);
      } else {
        console.error("ELEVENLABS_API_KEY missing — falling back");
      }
    } catch (err) {
      console.error("ElevenLabs TTS threw — falling back:", err);
    }

    // 2) Fallback provider so voice never goes silent for members.
    try {
      const { synthesizeWithLovableAi } = await import("@/lib/tts-fallback.server");
      const { buffer, mimeType } = await synthesizeWithLovableAi(data.text, voiceId);
      return { audioBase64: Buffer.from(buffer).toString("base64"), mimeType };
    } catch (err) {
      console.error("Fallback TTS failed:", err);
      return { error: "SERVICE_UNAVAILABLE", fallback: true };
    }
  });
